import {
  Badge,
  Banner,
  Button,
  IconButton,
  Page,
  Table,
  Tag,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';

import {
  chatbotsApi,
  SolvemateChatbotConfiguration,
  solvemateChatbotsApi,
} from 'apis/chatbots';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SettingsIntegrationsDocumentationButton } from '../../components/documentationButton/documentationButton';

import styles from './chatbots.module.scss';

export const defaultChatbotName = 'Chatbot';
export const pathToChatbotsSettings = '/settings/integrations/chatbots';

const emptyArray: SolvemateChatbotConfiguration[] = [];

/** Settings page that lists all integrated Chatbots. */
export const ChatbotsView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const { chatbots, isLoading, isError } =
    solvemateChatbotsApi.useReadSolvemateChatbotsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        chatbots: data?.data.chatbots || emptyArray,
      }),
    });
  const [deleteChatbot] = chatbotsApi.useDeleteChatbotMutation();

  const handleDeleteChatbot = async (
    chatbot: SolvemateChatbotConfiguration,
  ) => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${chatbot.name}"`,
            description: 'Are you sure you want to delete this chatbot?',
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      const name = chatbot.name || defaultChatbotName;
      try {
        await deleteChatbot({ id: chatbot.id });
        notify(
          {
            title: 'Success',
            text: `"${name}" has been deleted.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          { title: `Error while deleting "${name}".` },
          NotificationType.Error,
        );
      }
    }
  };

  return (
    <Page.Section
      title="All chatbots"
      titleMeta={
        <>
          {chatbots.length > 0 && (
            <Badge size="small" variant="secondary">
              {chatbots.length}
            </Badge>
          )}

          <SettingsIntegrationsDocumentationButton
            onClick={() =>
              elevioClient.openArticle(articles.settings.chatbots.documentation)
            }
          />
        </>
      }
      controls={
        <Button
          onClick={() =>
            navigate({
              path: `${pathToChatbotsSettings}/create-new`,
            })
          }
        >
          Add chatbot
        </Button>
      }
      callout={
        <Banner
          variant="announcement"
          icon="info"
          className={styles.notification}
          description={
            <>Currently we only support Solvemate chatbot integrations.</>
          }
        />
      }
    >
      <Table stickyHeader loading={isLoading} loadingFailed={isError}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Project id</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {chatbots.length > 0 ? (
            chatbots.map((chatbot, index) => (
              <Table.Tr key={index}>
                <Table.Td>{chatbot?.name || defaultChatbotName}</Table.Td>
                <Table.Td>{chatbot.projectId}</Table.Td>
                <Table.Td>
                  <Tag>{chatbot._type}</Tag>
                </Table.Td>
                <Table.Td align="right">
                  <Tooltip content="Delete">
                    <IconButton
                      variant="tertiary"
                      icon="trash"
                      onClick={() => handleDeleteChatbot(chatbot)}
                    />
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))
          ) : (
            <Table.Tr>
              <Table.Td colSpan={4} className={styles.noData}>
                No chatbot integrations created yet
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Page.Section>
  );
};
